import * as THREE from 'three'
import type { CameraControls } from '@react-three/drei'

type AnimationState = 'animating' | 'completed'

async function waitRest(ctrl: CameraControls) {
  await new Promise<void>(res => {
    const once = () => (ctrl.removeEventListener('rest', once), res())
    ctrl.addEventListener('rest', once)
  })
}

export default async function quizTransition(
  controls: CameraControls,
  finalTarget: THREE.Vector3,
  finalPosition: THREE.Vector3,
  direction: 'leftToRight' | 'downToUp',
  onTransitionStart?: () => void,
  onExitOffTop?: () => void,
  onTransition?: () => void,
  onTransitionEnd?: () => void,
  setAnimationState?: (s: AnimationState) => void
) {
  const cam = controls.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera
  const margin = 0.2
  const yOffsetFactor = 0.25
  onTransitionStart?.()

  // recompute spans at the current distance
  const spans = () => {
    const dist = controls.distance
    if ((cam as THREE.PerspectiveCamera).isPerspectiveCamera) {
      const p = cam as THREE.PerspectiveCamera
      const vSpan = 2 * dist * Math.tan(THREE.MathUtils.degToRad(p.fov / 2))
      const hSpan = vSpan * p.aspect
      return { vSpan, hSpan }
    } else {
      const o = cam as THREE.OrthographicCamera
      return { vSpan: Math.abs(o.top - o.bottom), hSpan: Math.abs(o.right - o.left) }
    }
  }
  const animate = async (fn: () => void) => {
    setAnimationState?.('animating')
    fn()
    await waitRest(controls)
    setAnimationState?.('completed')
  }

  // 1) exit off TOP
  await animate(() => {
    const { vSpan } = spans()
    const offTopY = 1 * vSpan + margin
    controls.setFocalOffset(0, offTopY, 0, true)
    onExitOffTop?.()
    // await waitRest(controls)
  })

  // controls.smoothTime = 1
  if (direction === 'leftToRight') {
    // 2) snap to your close-up pose; place RIGHT offscreen at raised Y (to look big)
    {
      const pos = new THREE.Vector3(-1.81, 0, 4.64)
      const look = new THREE.Vector3(0.17, 0.55, 4.55)
      controls.setLookAt(pos.x, pos.y, pos.z, look.x, look.y, look.z, false)

      const { vSpan, hSpan } = spans()
      const offRightX = 1 * hSpan + margin
      const raisedY = yOffsetFactor * vSpan
      controls.setFocalOffset(offRightX, raisedY, 0, false)
      controls.update(0)
    }
    await new Promise<void>(r => requestAnimationFrame(() => r()))
    onTransition?.()
    // 3) sweep RIGHT → LEFT offscreen (keep the raised Y)
    await animate(() => {
      const { vSpan, hSpan } = spans()
      const offLeftX = -(2.7 * hSpan + margin)
      const raisedY = yOffsetFactor * vSpan
      controls.setFocalOffset(offLeftX, raisedY, 0, true)
      // await waitRest(controls)
    })
  }

  if (direction === 'downToUp') {
    // 2) snap close-up; start BOTTOM offscreen (x centered), flush once
    {
      const pos = new THREE.Vector3(0, 2, 0)
      const look = new THREE.Vector3(0, 0, 0)
      controls.setLookAt(pos.x, pos.y, pos.z, look.x, look.y, look.z, false)
      controls.azimuthAngle = -3.14

      const { vSpan } = spans()
      const offBottomY = -(2 * vSpan + margin)
      controls.setFocalOffset(0, offBottomY, 0, false)
      controls.update(0)
    }
    await new Promise<void>(r => requestAnimationFrame(() => r()))
    onTransition?.()

    // 3) sweep BOTTOM → TOP offscreen
    await animate(() => {
      const { vSpan } = spans()
      const offTopY = 2 * vSpan + margin
      controls.setFocalOffset(0, offTopY, 0, true)
      // await waitRest(controls)
    })
  }

  // 4) re-anchor using finalPosition -> look at finalTarget, then place BELOW
  {
    controls.setLookAt(
      finalPosition.x,
      finalPosition.y,
      finalPosition.z,
      finalTarget.x,
      finalTarget.y,
      finalTarget.z,
      false
    )
    controls.update(0)
    const { vSpan } = spans()
    const offBottomY = -(0.5 * vSpan + margin)
    controls.setFocalOffset(0, offBottomY, 0, false)
  }

  // 5) rise smoothly to CENTER
  // controls.setFocalOffset(0, 0, 0, true).then(() => {
  //   onTransitionEnd?.()
  // })
  // await waitRest(controls)
  await animate(() => {
    controls.setFocalOffset(0, 0, 0, true)
  })
  onTransitionEnd?.()
}
